import type { Command } from "../document/commands.ts";
import type { MapDocument } from "../document/types.ts";
import { zoneTypeAt, zoneValue, type ZoneCellChange } from "../document/zones.ts";
import { requireLayer } from "./panel-commands.ts";

/**
 * Zone painting (ADR-054's amendment of 2026-09-27, decision 4): the command one stroke, shape or erase of a
 * zone layer makes. The cells are the collision brushes' (`CollisionAuthoring.stamp` and `span`); only the value
 * differs, a registered type id or none.
 */

/**
 * Paints cells of a zone layer with a type, or erases them, as one command.
 *
 * @param map The map.
 * @param layerId The zone layer.
 * @param indices Cell indices in the collision grid, repeats allowed.
 * @param type The type id to paint, or null to erase.
 * @returns The command, or null when every cell already holds the value.
 * @throws Error when the layer is not a zone layer, an index lies outside the map or the type is blank.
 */
export function paintZoneCommand(map: MapDocument, layerId: string, indices: readonly number[], type: string | null): Command | null {
  const layer = requireLayer(map, layerId);
  if (layer.kind !== "zone") {
    throw new Error(`layer ${layerId} is ${layer.kind}, not zone`);
  }
  const after = zoneValue(type, "a zone stroke paints");
  const cells: ZoneCellChange[] = [];
  const seen = new Set<number>();
  for (const index of indices) {
    if (!Number.isInteger(index) || index < 0 || index >= layer.cells.length) {
      throw new Error(`cell index ${index} is outside the ${map.width}x${map.height} map`);
    }
    if (seen.has(index)) {
      continue;
    }
    seen.add(index);
    const before = zoneTypeAt(layer, index);
    if (before !== after) {
      cells.push({ index, before, after });
    }
  }
  return cells.length === 0 ? null : { kind: "paintZone", mapId: map.id, layerId, cells };
}
